/**
 * @file A general container, able to generate a set of charts by reading the /plots/{variable} endpoint.
 *
 * Loads the list of charts, then asynchronously generate all of them.
 */

import { useState, useEffect, Suspense, lazy, useContext } from 'react';
import PropTypes from 'prop-types';
import { ApiContext } from '../ApiContext';
import { fetchAPI } from '../utils';
import Loading from '../components/Loading';
import Message from '../components/Message';
import { notifyResizeEvent } from '../utils.js';

const Chart = lazy(() => import('./Chart.jsx'));

function ChartsGenerator({
  month,
  region,
  regionName,
  selectedLayer,
  lat,
  lon,
  temporalAggregation,
  threshold,
  altitude,
  level,
  floodRecurrence,
  hydroModel,
  variable,
}) {
  const [plots, setPlots] = useState([]);
  const [status, setStatus] = useState('unloaded');
  const { apiBase } = useContext(ApiContext);
  const [plotParams, setPlotParams] = useState({});

  useEffect(() => {
    setStatus('unloaded');
  }, [
    variable,
    region,
    regionName,
    selectedLayer,
    lat,
    lon,
    temporalAggregation,
    month,
    threshold,
    altitude,
    level,
    floodRecurrence,
    hydroModel,
  ]);

  useEffect(() => {
    async function loadPlots() {
      const qsData = {
        region,
        regionName,
        selectedLayer,
        lat: lat.toFixed(2),
        lon: lon.toFixed(2),
        temporalAggregation,
        ...(threshold && { threshold }),
        ...(!isNaN(altitude) && { altitude }),
        ...(level && { level }),
        ...(floodRecurrence && { floodRecurrence }),
        ...(hydroModel && { hydroModel }),
        ...(temporalAggregation === 'monthly' && { month }),
        ...(temporalAggregation === 'seasonal' && { month }),
      };
      const searchParams = new URLSearchParams(qsData);
      setPlotParams(searchParams.toString());

      setStatus('loading');
      try {
        const plots = await fetchAPI(`${apiBase}/plots/${variable}?${searchParams.toString()}`);
        const json = await plots.json();
        setStatus('loaded');
        setPlots(json);
      } catch (error) {
        setStatus('error');
        console.error(error);
        return;
      }
      notifyResizeEvent();
    }
    if (status === 'unloaded') {
      if (temporalAggregation) {
        loadPlots();
      }
    }
  }, [
    altitude,
    apiBase,
    lat,
    level,
    lon,
    month,
    region,
    regionName,
    selectedLayer,
    status,
    temporalAggregation,
    threshold,
    floodRecurrence,
    hydroModel,
    variable,
  ]);

  if (status === 'error') {
    return <Message>Unable to load plots</Message>;
  }

  return (
    <div style={{ position: 'relative' }}>
      {status === 'loading' && (
        <Loading
          message="Loading plots…"
          {...{
            position: 'absolute',
            top: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            padding: '1rem',
            width: '100%',
            zIndex: 100,
          }}
        />
      )}
      <Suspense fallback={<Loading />}>
        {plots.map((plot) => {
          const { id, url } = plot;
          return (
            <Chart
              key={id}
              id={id}
              url={url}
              useResizeHandler={true}
              variable={variable}
              params={plotParams}
            />
          );
        })}
      </Suspense>
    </div>
  );
}

ChartsGenerator.propTypes = {
  month: PropTypes.number,
  region: PropTypes.string.isRequired,
  regionName: PropTypes.string.isRequired,
  selectedLayer: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  lat: PropTypes.number,
  lon: PropTypes.number,
  temporalAggregation: PropTypes.string.isRequired,
  threshold: PropTypes.string,
  altitude: PropTypes.number,
  level: PropTypes.string,
  floodRecurrence: PropTypes.string,
  hydroModel: PropTypes.string,
  variable: PropTypes.string.isRequired,
};

export default ChartsGenerator;
